import { Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import DownloadIcon from 'assets/images/download.png';
import { ReactComponent as ReviseIcon } from 'assets/images/revise.svg';
import { ReactComponent as ViewIcon } from 'assets/images/view.svg';
import { Loader } from 'components/common/Loader';
import { UploadDocSchema } from 'constants/ValidationSchema';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Button, Card, Form, Modal } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useUploadDocumentsMutation,
  useUploadAssetMutation,
  useUpdateDocumentsMutation,
} from 'redux/services/project';
import './DocumentsUpload.scss';

interface DocumentProps {
  isSingleEdit?: boolean;
  projectDetails?: any | null;
  setRefresh: any;
  refresh: boolean;
  isLaunched?: boolean;
  disabled?: boolean;
  onUploadDoc?: () => void;
}

export type Document = {
  id?: string;
  docNo?: string;
  title: string;
  rev?: string;
  file: File | string;
};

const DocumentsUpload = ({
  isSingleEdit,
  projectDetails,
  setRefresh,
  refresh,
  isLaunched,
  disabled,
  onUploadDoc,
}: DocumentProps) => {
  const { t } = useTranslation();
  const { step, setupId: projectId } = useParams();
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const [uploadDocuments] = useUploadDocumentsMutation();
  const [updateDocument] = useUpdateDocumentsMutation();
  const [uploadAsset, { isLoading: uploading }] = useUploadAssetMutation();

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: 'image/png, image/jpeg, application/pdf, application/dwg',
    onDrop: onDropHandler,
  });

  function onDropHandler(acceptedFiles: File[]) {
    const fileSize = acceptedFiles[0].size / 1024 ** 2;

    if (fileSize <= 5) {
      formik.setFieldValue('file', acceptedFiles[0]);
    } else {
      toast.error(t('error_file_size_exceeded'));
    }
  }

  const handleClose = () => {
    setShow(false);
    acceptedFiles.length = 0;
  };

  const handleShow = () => setShow(true);

  // const fileExtension = (url: any) => {
  //   return url.split('.').pop().split(/\#|\?/)[0];
  // };

  const createDocument = (val: typeof formik.values, uploadRes: any) => {
    const body = {
      projectId,
      values: {
        ...val,
        file: { path: uploadRes.data.url },
      },
    };

    return uploadDocuments(body);
  };

  const updateDocuments = (val: typeof formik.values, uploadRes: any) => {
    const body = {
      projectId,
      values: {
        ...val,
        file: { path: uploadRes.data.url },
      },
    };

    return updateDocument(body);
  };

  const formik = useFormik({
    initialValues: {
      docNo: '',
      title: '',
      rev: '',
      file: null,
      isRevise: false,
    },
    validationSchema: UploadDocSchema,
    onSubmit: async (val) => {
      if (val.isRevise) {
        const doc = projectDetails.find((d: any) => d.docNo === val.docNo);

        if (doc && doc.rev === val.rev) {
          toast.error('Please update the document revision');
          formik.setFieldError('rev', 'Please update the document revision');
          return;
        }
      }
      const formData = new FormData();
      formData.append('file', val.file!);
      formData.append('path', `projects/${projectId}`);

      const uploadRes: any = await uploadAsset(formData);

      if (uploadRes.data) {
        const res: any = val.isRevise
          ? await updateDocuments(val, uploadRes)
          : await createDocument(val, uploadRes);

        if (res.data) {
          toast.success(t('success_upload_document'));
          formik.resetForm();
          handleClose();
          setRefresh(!refresh);
          onUploadDoc?.();
        } else {
          toast.error(res.error.data.message || t('something_went_wrong'));
        }
      } else {
        toast.error(uploadRes.error.data.message || t('something_went_wrong'));
      }
    },
  });

  const getReviseClickHandler = (doc: Document) => () => {
    setShow(true);
    formik.setFieldValue('docNo', doc.docNo);
    formik.setFieldValue('rev', doc.rev);
    formik.setFieldValue('title', doc.title);
    formik.setFieldValue('isRevise', true);
  };

  return (
    <>
      <div className="d-flex flex-column documents-upload h-100 overflow-auto">
        {uploading && <Loader />}
        <div className="text-end mb-3">
          <Button
            variant="primary"
            hidden={disabled}
            onClick={() => {
              formik.resetForm();
              handleShow();
            }}
          >
            {t('btn_upload_documents')}
          </Button>
        </div>
        {projectDetails?.length > 0 ? (
          <div className="d-flex flex-wrap overflow-auto mb-2">
            {projectDetails.map((item: any) => (
              <div className="d-flex p-2 card-container" key={item.docNo}>
                <Card className="docs-list">
                  <Card.Body className="doc px-2 pt-3 pb-2">
                    <div className="d-flex align-items-center mb-2">
                      <span className="doc-list-title">
                        {t('label_doc_no')}:
                      </span>
                      &nbsp;
                      <p className="mb-0">{item.docNo}</p>
                    </div>
                    <div className="d-flex justify-content-between">
                      <div className="d-flex align-items-center">
                        <span className="doc-list-title">
                          {t('label_doc_title')}:
                        </span>
                        &nbsp;
                        <p className="mb-0">{item.title}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="doc-list-title">
                          {t('label_doc_rev')}:
                        </span>
                        &nbsp;
                        <p className="mb-0">{item.rev}</p>
                      </div>
                    </div>
                  </Card.Body>
                  <Card.Footer className="p-0 d-flex">
                    <Button
                      variant="link"
                      hidden={disabled}
                      className="text-white flex-grow-1 text-decoration-none"
                      onClick={getReviseClickHandler(item)}
                    >
                      <ReviseIcon width="16" height="16" />
                      <span className="ms-2">{t('btn_revise')}</span>
                    </Button>
                    <a
                      href={item.file.path}
                      target="_blank"
                      rel="noreferrer"
                      download={`${item.docNo}-${item.title}-${item.rev}`}
                      className="text-white flex-grow-1 text-decoration-none btn btn-link"
                    >
                      <img src={DownloadIcon} alt="" />
                      <span className="ms-2">{t('btn_download')}</span>
                    </a>
                    <a
                      href={item.file.path}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white flex-grow-1 text-decoration-none btn btn-link"
                    >
                      <ViewIcon width="16" height="16" />
                      <span className="ms-2">{t('btn_view')}</span>
                    </a>
                  </Card.Footer>
                </Card>
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center w-100 h-100 text-center">
            <div className="text-center flex-grow-1 overflow-auto no-docs-img" />
            <p className="font-bold mt-2 mb-0">
              {t('documents_upload_title1')} <br />
              {t('documents_upload_title2')}
            </p>
          </div>
        )}
      </div>
      {isSingleEdit ? (
        <div className="d-flex justify-space-between">
          <Button
            variant="outline-primary"
            onClick={() => {
              navigate(-1);
            }}
          >
            {t('btn_back')}
          </Button>
          <Button
            variant="primary"
            className="ms-auto"
            hidden={disabled}
            onClick={() => {
              navigate(-1);
            }}
          >
            {t('btn_save-exit')}
          </Button>
        </div>
      ) : (
        <div className="d-flex justify-space-between">
          <Button
            variant="outline-primary"
            onClick={() => {
              navigate({
                pathname: `/project/setup/${projectId}/${Number(step) - 1}`,
              });
            }}
          >
            {t('btn_back')}
          </Button>
          <Button
            variant="primary"
            className="ms-auto"
            onClick={() => {
              isLaunched
                ? navigate(`/projects`)
                : navigate({
                    pathname: `/project/setup/${projectId}/${Number(step) + 1}`,
                  });
            }}
          >
            {isLaunched ? t('btn_save-exit') : t('btn_save-next')}
          </Button>
        </div>
      )}

      <Modal
        className="upload-document-modal"
        show={show}
        onHide={handleClose}
        centered
      >
        <Modal.Header className="document-upload-header justify-content-center">
          <Modal.Title>{t('modal_title_add_new_doc')}</Modal.Title>
          <IconButton onClick={handleClose}>
            <Close className="text-white" color="inherit" />
          </IconButton>
        </Modal.Header>
        <form onSubmit={formik.handleSubmit}>
          <Modal.Body className="px-4 pt-4 docs-upload">
            <div className="d-flex align-items-center mb-3">
              <Form.Label className="mb-0 text-start mr-3">
                {t('label_doc_no')} <span>*</span>
              </Form.Label>
              <div className="w-100">
                <Form.Control
                  name="docNo"
                  disabled={formik.values.isRevise}
                  value={formik.values.docNo}
                  onChange={formik.handleChange}
                />
                {formik.touched.docNo && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {formik.errors.docNo}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center mb-3">
              <Form.Label className="mb-0 text-start mr-3">
                {t('label_doc_title')} <span>*</span>
              </Form.Label>
              <div className="w-100">
                <Form.Control
                  name="title"
                  disabled={formik.values.isRevise}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                />
                {formik.touched.title && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {formik.errors.title}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center mb-3">
              <Form.Label className="mb-0 text-start mr-3">
                {t('label_doc_rev')} <span>*</span>
              </Form.Label>
              <div className="w-100">
                <Form.Control
                  name="rev"
                  value={formik.values.rev}
                  onChange={formik.handleChange}
                />
                {formik.touched.rev && (
                  <Form.Control.Feedback type="invalid" className="d-block">
                    {formik.errors.rev}
                  </Form.Control.Feedback>
                )}
              </div>
            </div>
            <Form.Label>
              {t('label_upload_doc')} <span>*</span>
            </Form.Label>
            <div
              {...getRootProps({
                className: 'dropzone position-relative docs-browse text-end',
              })}
            >
              <p className="mb-0 px-2">{acceptedFiles[0]?.name}</p>
              <span className="btn btn-primary d-inline-block min-w-100">
                {t('label_browse')}
              </span>
              <input {...getInputProps()} className="position-absolute" />
            </div>
            {formik.touched.file && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {formik.errors.file}
              </Form.Control.Feedback>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center pb-3">
            <Button variant="primary" type="submit">
              {t('submit')}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default DocumentsUpload;
